import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useRouter, type Href } from 'expo-router';
import { Hospital, Stethoscope } from 'lucide-react-native';
import React from 'react';
import { twJoin } from 'tailwind-merge';

import type { ExpertDirectoryCardProps } from './types';
import { FeaturedBadge } from '../featuredBadge';

import { primum_default_avatar } from '@/assets/images';
import { TrackedLink as TrackedRouterLink } from '@/components/TrackedLink';
import { ExpertMetrics, useHasExpertMetrics } from '@/components/profile/ExpertMetrics';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Button, ButtonGroup, ButtonText } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Divider } from '@/components/ui/divider';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { segmentClient } from '@/util/analytics';
import { getNativeTextProps } from '@/util/getNativeTextProps';
import type { ExpertProfile } from '@/util/mapDirectoryProfiles';
import { isSurveyProfile } from '@/util/userFilters';

/**
 * One cell of the card's metric row, sized to its content.
 *
 * It used to be pinned to half the footer, `flex-1 basis-[calc(50%-0.375rem)]`, so cells
 * lined up across neighbouring cards in the grid. That alignment cost more than it bought.
 * A cell could not claim slack from a shorter neighbour, so at a 1024px viewport each got
 * 130px while "Reference Champion" and its mark need 141px, and the label was silently
 * truncated on every card in a three-column grid.
 *
 * Content sizing hands both metrics the whole row instead. At that same 1024px the row is
 * 358px and the pair needs 274px, so the full label fits inline with room to spare, and the
 * `flex-wrap` on the row still catches the case where it genuinely does not.
 *
 * `min-w-0` because a flex child defaults to `min-width: auto` and would push past the row
 * rather than let its label truncate. This keeps truncation as the last resort it should be,
 * instead of the everyday behavior it had become.
 */
const METRIC_ITEM = 'min-w-0';

/**
 * The card's base: a tinted strip holding the metrics and, on narrow viewports, the
 * actions. The tint separates the metrics from the body copy above; without it they read
 * as more gray rows rather than a distinct section, and tinting the strip rather than each
 * metric avoids a button shape on a static fact inside a card that is itself pressable.
 *
 * Mobile always renders this tinted and bordered, since it carries the buttons. Desktop
 * uses it for metrics only, so a card with neither a response time nor the badge needs
 * `EMPTY_DESKTOP_FOOTER` instead.
 */
const CARD_FOOTER = 'mt-4 border-t border-outline-100 bg-background-50 px-4 py-3';

/**
 * Desktop-only footer with nothing to show: same footprint as `CARD_FOOTER`, no chrome.
 *
 * A grid row stretches every card to its tallest sibling, so an empty footer still has to
 * hold its floor of that height or its border lands at a different offset than its row
 * mates'. `min-h` reserves it; making the border and tint transparent rather than deleting
 * them keeps the box model identical between the two states.
 */
const EMPTY_DESKTOP_FOOTER = 'md:min-h-[43px] md:border-transparent md:bg-transparent';

type CardContentProps = {
  item: ExpertProfile;
  isMultiColumn: boolean;
  buildInquiryUrl: (expertId: string | number) => Href;
  showFeaturedBadge?: boolean;
};

/**
 * Internal card content component for ExpertDirectoryCard.
 * Renders the expert's profile information, badges, and action buttons.
 * Memoized to prevent unnecessary re-renders during list scrolling.
 */
const CardContent = React.memo(function CardContent({
  item,
  isMultiColumn,
  buildInquiryUrl,
  showFeaturedBadge,
}: CardContentProps) {
  const { navigate } = useRouter();

  const hasMetrics = useHasExpertMetrics({
    expertId: item.id,
    responseTime: item.responseTime?.toString(),
  });

  const isSurvey = isSurveyProfile(item.name);

  return (
    <Card
      variant="outline"
      testID={isSurvey ? 'survey-card' : 'expert-card'}
      className={twJoin(
        isMultiColumn && 'hover:shadow-hard-1',
        'overflow-hidden p-0 pt-4 web:h-full web:flex-1'
      )}>
      <HStack className="items-center gap-6 px-4 web:gap-3">
        <Avatar size="md">
          <AvatarImage source={item.avatarSrc ? { uri: item.avatarSrc } : primum_default_avatar} />
        </Avatar>
        <VStack className="flex-1 justify-center" space="xs">
          <Text
            size={isMultiColumn ? 'md' : 'lg'}
            testID={isSurvey ? 'survey-card-name' : 'expert-card-name'}
            className="flex-1 font-medium text-typography-900"
            {...getNativeTextProps()}
            isTruncated>
            {item.name}
          </Text>
          <HStack className="flex-wrap items-center gap-2">
            {showFeaturedBadge ? <FeaturedBadge /> : null}
            {item.speciality ? (
              <Badge action="info" className="gap-2 px-2" size="lg">
                <BadgeText className="text-xs text-info-800">{item.speciality}</BadgeText>
              </Badge>
            ) : null}
          </HStack>
        </VStack>
      </HStack>
      <Divider className="my-4" />
      {/*
       * Absorbs the row-stretch slack from `web:h-full web:flex-1` above, so a shorter
       * body doesn't push it below the footer instead: see `EMPTY_DESKTOP_FOOTER`.
       */}
      <VStack className="mb-4 px-4 web:flex-1">
        <VStack className="gap-2">
          {item.description && (
            <Text className="text-sm text-typography-700">{item.description}</Text>
          )}
          {item.institution ? (
            <HStack className="items-start gap-2">
              <Icon
                as={Hospital}
                className="shrink-0 text-typography-600"
                {...({ accessibilityLabel: 'Institution' } as any)}
              />
              <Text className="text-sm text-typography-700">{item.institution}</Text>
            </HStack>
          ) : null}
          {item.title ? (
            <HStack className="items-start gap-2">
              <Icon
                as={Stethoscope}
                className="shrink-0 text-typography-600"
                {...({ accessibilityLabel: 'Specialty' } as any)}
              />
              <Text className="text-sm text-typography-700">{item.title}</Text>
            </HStack>
          ) : null}
          {item.focusArea ? (
            <HStack className="items-center">
              <Text className="ml-7 text-sm text-typography-700">
                Focus areas:{' '}
                <Text className="font-medium text-sm text-typography-700">{item.focusArea}</Text>
              </Text>
            </HStack>
          ) : null}
        </VStack>
      </VStack>
      <VStack
        data-component="directory-card-footer"
        className={twJoin(CARD_FOOTER, !hasMetrics && EMPTY_DESKTOP_FOOTER)}>
        {hasMetrics ? (
          <ExpertMetrics
            expertId={item.id}
            responseTime={item.responseTime?.toString()}
            metricClassName={METRIC_ITEM}
          />
        ) : null}

        <ButtonGroup
          className={twJoin('flex-row justify-center gap-4 md:hidden', hasMetrics && 'mt-3')}>
          <Button
            size="xs"
            onPress={(e) => {
              segmentClient.track(ANALYTICS_EVENTS.EXPERT_SELECTED, {
                expert_id: item.id,
              });

              navigate(`/profile/${item.id}`);

              e.preventDefault();
            }}
            variant="outline"
            className="flex-1 bg-background-0 web:md:hidden">
            <ButtonText>View Profile</ButtonText>
          </Button>
          <Button
            size="xs"
            onPress={(e) => {
              segmentClient.track(ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED, {
                expert_id: item.id,
                expert_name: item.name,
              });

              navigate(buildInquiryUrl(item.id));

              e.preventDefault();
            }}
            className="flex-1 web:flex-auto">
            <ButtonText>Message</ButtonText>
          </Button>
        </ButtonGroup>
      </VStack>
    </Card>
  );
});

/**
 * ExpertDirectoryCard - Complete card for regular medical experts
 *
 * Features:
 * - Info badge for speciality
 * - Title and focus areas display
 * - Response time from data
 * - Profile navigation
 * - Platform-specific link wrapping for multi-column layout
 */
export const ExpertDirectoryCard = React.memo(function ExpertDirectoryCard({
  item,
  isMultiColumn,
  buildInquiryUrl,
  showFeaturedBadge,
}: ExpertDirectoryCardProps) {
  const content = (
    <CardContent
      item={item}
      isMultiColumn={isMultiColumn}
      buildInquiryUrl={buildInquiryUrl}
      showFeaturedBadge={showFeaturedBadge}
    />
  );

  if (isMultiColumn) {
    return (
      <TrackedRouterLink
        asChild
        href={buildInquiryUrl(item.id)}
        trackingEvent={ANALYTICS_EVENTS.DIRECTORY_MESSAGE_SELECTED}
        trackingData={{ expert_id: item.id, expert_name: item.name }}>
        <Pressable className={twJoin('h-full', isWeb && 'flex-1 justify-between')}>
          {content}
        </Pressable>
      </TrackedRouterLink>
    );
  }

  return content;
});
